import { TargetGroup } from '@/helper/enums';

import {
  BANKING,
  BILLING_DIFFERENT_RECIPIENT_NAME,
  CONTRACT,
  CONTRACT_TYPE_CHANGE,
  CUSTOMER_TYPE,
  INCENTIVE_CODE,
  INCENTIVE_CODE_TEXT,
  INCENTIVE_VALUE,
  INVOICE,
  KWK_CODE,
  PERSONAL,
  SELF_SERVICE_REGISTRATION_DISPLAY_TYPE,
  SHOW_MARKETING_OPTIN,
  TEXT_REGULATED_PRODUCTS,
} from './PageContractConclusionConstants';
import {
  type Address,
  type ContractData,
  CustomerType,
  type OrderData,
  PageContractConclusionOrderType,
  type PaymentData,
  type PersonalData,
} from './PageContractConclusionTypes';

/**
 * Maps API order type ("Z1", "Z2") to correct enum values
 */
export const mapOrderType = (orderType: string): PageContractConclusionOrderType => {
  switch (orderType) {
    case 'Z1':
      return PageContractConclusionOrderType.ProviderChange;
    case 'Z2':
      return PageContractConclusionOrderType.NewTenant;
    case 'Z3':
      return PageContractConclusionOrderType.Retention;
    case 'Z4':
      return PageContractConclusionOrderType.ProductChange;
    default:
      return PageContractConclusionOrderType.ProviderChange;
  }
};

export const normalizeOrderData = (data: any): OrderData => {
  /**
   * Maps API customer type ("001", "002") to correct enum values
   */
  const mapCustomerType = (type: string): CustomerType => {
    switch (type) {
      case '001':
        return CustomerType.Individual;
      case '002':
        return CustomerType.Organization;
      case '003':
        return CustomerType.Group;
      default:
        return CustomerType.Individual;
    }
  };
  return {
    orderType: mapOrderType(data[CONTRACT_TYPE_CHANGE]),
    customerType: mapCustomerType(data[CUSTOMER_TYPE] as string),

    personalData: {
      salutation: data[PERSONAL.TITLE] || '',
      firstname: data[PERSONAL.FIRSTNAME] || '',
      lastname: data[PERSONAL.LASTNAME] || '',
      company: data[PERSONAL.COMPANY] || '',
      supplement: data[PERSONAL.COMPANY_SUPPLEMENT] || '',
    } as PersonalData,

    contractPartnerData: {
      title: data[PERSONAL.PARTNER_TITLE] || '',
      firstname: data[PERSONAL.PARTNER_FIRSTNAME] || '',
      lastname: data[PERSONAL.PARTNER_LASTNAME] || '',
    } as PersonalData,

    regulatedProducts: data[TEXT_REGULATED_PRODUCTS] || '',

    paymentData: {
      iban: data[BANKING.IBAN] || '',
      sepaCheck: data[BANKING.SEPA_CHECK] === 'true',
    } as PaymentData,

    invoiceAddress: {
      street: data[INVOICE.STREET] || '',
      hnr: data[INVOICE.HNR] || '',
      zip: data[INVOICE.ZIP] || '',
      city: data[INVOICE.CITY] || '',
      country: data[INVOICE.COUNTRY] || '',
      differentInvoiceAddress: !!data[BILLING_DIFFERENT_RECIPIENT_NAME],
      postBox: data[INVOICE.POST_BOX],
    } as Address,

    incentiveData: {
      code: data[INCENTIVE_CODE] || '',
      value: data[INCENTIVE_VALUE] || '0',
      descriptionText: data[INCENTIVE_CODE_TEXT] || '',
    },

    customerRecommendationViaCodeData: {
      kwkCode: data[KWK_CODE] || '',
      solicitorGpNr: data[CONTRACT.SOLICITOR_GP_NR] || '',
      solicitorEmail: data[CONTRACT.SOLICITOR_EMAIL] || '',
      rewardType: data[CONTRACT.REWARD_TYPE] || '',
      solicitorSplittingFactor: Number(data[CONTRACT.SOLICITOR_SPLITTING_FACTOR]) || 0,
      solicitorContractNr: data[CONTRACT.SOLICITOR_CONTRACT_NR] || '',
    },

    contractData: (data.contract || []).map((contract: any): ContractData => ({
      shippingAddress: {
        street: contract[CONTRACT.SHIPPING_ADDRESS_STREET] || '',
        hnr: contract[CONTRACT.SHIPPING_ADDRESS_HNR] || '',
        zip: contract[CONTRACT.SHIPPING_ADDRESS_ZIP] || '',
        city: contract[CONTRACT.SHIPPING_ADDRESS_CITY] || '',
        additional: '',
      },
      deliveryDesiredBeginning: contract[CONTRACT.DELIVERY_DESIRED_BEGINNING] || '',
      counterReading: contract[CONTRACT.COUNTER_READING] || '',
      counterNumber: contract[CONTRACT.COUNTER_NUMBER] || '',
      utilitiesChangeAccountProvider: contract[CONTRACT.UTILITIES_CHANGE_ACCOUNT_PROVIDER] || '',
      utilitiesChangeCancellationType: contract[CONTRACT.UTILITIES_CHANGE_CANCELLATION_TYPE] || '',
      utilitiesChangeCancellationDoneWhen: contract[CONTRACT.UTILITIES_CHANGE_CANCELLATION_DONE_WHEN] || '',
      tariffProductId: contract[CONTRACT.PRODUCT_ID] || '',
      tariffProductName: contract[CONTRACT.PRODUCT_NAME] || '',
      tariffProductCategory: contract[CONTRACT.PRODUCT_CATEGORY].toLowerCase(),
      tariffProductBasePrice: contract[CONTRACT.PRODUCT_BASE_PRICE] || '0',
      tariffProductWorkingPrice: contract[CONTRACT.PRODUCT_WORKING_PRICE] || '0',
      tariffProductMonthlyPrice: contract[CONTRACT.PRODUCT_MONTHLY_PRICE] || '0',
      tariffProductYearlyPrice: contract[CONTRACT.PRODUCT_YEARLY_PRICE] || '0',
      tariffConsumption: contract[CONTRACT.EXPECTED_CONSUMPTION] || '',
      tariffPricesDate: contract[CONTRACT.PRODUCT_PRICE_DATE] || '',
      tariffProductTargetGroup: contract[CONTRACT.PRODUCT_TARGET_GROUP] || '',
      tariffProductBonus: contract[CONTRACT.BONUS] || '',
      tariffProductBonusDirect: contract[CONTRACT.BONUS_DIRECT] || '',
      textMsb: contract[CONTRACT.TEXT_MSB] || '',
      textProductNotice: contract[CONTRACT.TEXT_PRODUCT_NOTICE] || '',
      textPriceGuarantee: contract[CONTRACT.TEXT_PRICE_GUARANTEE] || '',
      textAgb: contract[CONTRACT.TEXT_AGB] || '',
      textMinDuration: contract[CONTRACT.TEXT_MIN_DURATION] || '',
      showOptional: false,
      terminationNotice: '',
    })),

    selfServiceRegistration: data[SELF_SERVICE_REGISTRATION_DISPLAY_TYPE]?.trim() || 'display_agb',
    optInVisible: !!data[SHOW_MARKETING_OPTIN],
  };
};
